<template>
  <div class="schedule-page">
    <div class="header" :class="{ 'header-empty': !hasHotelHeaderContent }" :style="headerStyle">
      <div v-if="hasHotelHeaderContent" class="header-hotel">
        <img
          v-if="brandUrl"
          :src="brandUrl"
          class="hotel-logo"
          @error="brandUrl = ''"
        />
        <span class="hotel-name">{{ hotelName }}</span>
      </div>
    </div>

    <div class="content-area">
      <div class="login-shell">
        <div class="brand-panel">
          <div class="brand-wordmark">
            <span class="brand-prefix">Schedule</span>
            <span class="brand-accent">Assist</span>
          </div>
          <div class="login-subtitle">Sign in to continue</div>
        </div>

        <van-form @submit="onSubmit" class="login-form">
          <div class="field-box">
            <div class="field-icon">@</div>
            <van-field
              v-model="form.username"
              class="login-field"
              placeholder="Enter username"
              :border="false"
              :rules="[{ required: true, message: 'Username is required' }]"
            />
          </div>

          <div class="field-box">
            <div class="field-icon">*</div>
            <van-field
              v-model="form.password"
              class="login-field"
              type="password"
              placeholder="Enter password"
              :border="false"
              :rules="[{ required: true, message: 'Password is required' }]"
            />
          </div>

          <div class="submit-area">
            <van-button
              round
              block
              type="info"
              native-type="submit"
              :loading="submitting"
              :color="submitColor"
              class="submit-button"
            >
              Login
            </van-button>
          </div>
        </van-form>

        <div class="powered-by">Powered by Hospitality Voice Tech Inc.</div>
      </div>
    </div>
  </div>
</template>

<script>
import { Toast } from "vant";
import { checklistLogin, getCurrentScheduleUser, getHotelInfo } from "@/api/schedule";
import { clearScheduleAuthSession, setScheduleAuthSession } from "@/utils/scheduleAuth";

export default {
  name: "ScheduleLogin",
  data() {
    return {
      hotelNo: "",
      hotelName: "",
      brandUrl: "",
      hotelColor: "",
      submitting: false,
      form: {
        username: "",
        password: "",
      },
    };
  },
  computed: {
    hasHotelHeaderContent() {
      return Boolean(this.hotelName || this.brandUrl);
    },
    headerStyle() {
      if (this.hotelColor && this.hasHotelHeaderContent) {
        return {
          background: `#${this.hotelColor}`,
        };
      }
      return {
        background: "#ffffff",
      };
    },
    submitColor() {
      return this.hotelColor ? `#${this.hotelColor}` : "#ff7a00";
    },
  },
  created() {
    this.hotelNo = this.$route.query.hotel || "";
    if (!this.hotelNo) {
      Toast.fail("Missing hotel information. Please scan the QR code again.");
      return;
    }
    this.loadHotelInfo();
  },
  methods: {
    async loadHotelInfo() {
      try {
        const res = await getHotelInfo(this.hotelNo);
        const isSuccess = Number(res.status) === 0 || res.success === true;
        if (isSuccess && res.data) {
          this.hotelName = res.data.hotelName || "";
          this.hotelColor = res.data.color || "";
          if (res.data.brand) {
            this.brandUrl =
              "https://hotelfile353485474178.s3-accelerate.amazonaws.com/logo/" +
              res.data.brand.toLowerCase().replace(/ /g, "_") +
              ".png";
          }
        }
      } catch (e) {
        // The page can still render without remote branding.
      }
    },
    async onSubmit() {
      if (!this.hotelNo) {
        Toast.fail("Missing hotel information. Please scan the QR code again.");
        return;
      }
      this.submitting = true;
      clearScheduleAuthSession();
      try {
        const loginRes = await checklistLogin({
          hotelDeptName: this.form.username.trim(),
          hotelName: this.hotelNo,
          password: this.form.password,
        });
        const loginSuccess = Number(loginRes.status) === 0 || loginRes.success === true;
        const token = loginRes.data && loginRes.data.token;
        if (!loginSuccess || !token) {
          Toast.fail(loginRes.msg || "Login failed. Please try again.");
          return;
        }

        const currentUserRes = await getCurrentScheduleUser(token);
        const userSuccess = Number(currentUserRes.status) === 0 || currentUserRes.success === true;
        const userId = currentUserRes.data && currentUserRes.data.id;
        if (!userSuccess || !userId) {
          Toast.fail(currentUserRes.msg || "Failed to load current user.");
          return;
        }

        setScheduleAuthSession(token, userId);
        this.$router.replace({
          name: "ScheduleRequest",
          query: {
            hotel: this.hotelNo,
            login: "1",
          },
        });
      } catch (e) {
        Toast.fail("Network error. Please try again.");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.schedule-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  box-sizing: border-box;
}

.header {
  background: #1989fa;
  color: #fff;
  padding: 14px 16px 12px;
  min-height: 60px;
  box-sizing: border-box;
}

.header-empty {
  color: transparent;
  border-bottom: 1px solid #f2f4f7;
}

.header-hotel {
  display: flex;
  align-items: center;
}

.hotel-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
  margin-right: 8px;
  border-radius: 4px;
  background: #fff;
  padding: 2px;
  flex-shrink: 0;
}

.header-title {
  font-size: 17px;
  font-weight: 600;
  text-align: center;
  margin-top: 8px;
}

.content-area {
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 52px 24px 20px;
}

.login-shell {
  width: 100%;
  max-width: 420px;
}

.brand-panel {
  text-align: center;
  margin-bottom: 40px;
}

.brand-wordmark {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 56px;
}

.brand-prefix,
.brand-accent {
  font-size: 42px;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1;
}

.brand-prefix {
  color: #5451a6;
}

.brand-accent {
  color: #ff7a00;
  margin-left: 6px;
}

.hotel-name {
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  line-height: 1.3;
  word-break: normal;
}

.login-subtitle {
  margin-top: 8px;
  font-size: 14px;
  color: #98a2b3;
}

.login-form {
  width: 100%;
}

.field-box {
  display: flex;
  align-items: center;
  min-height: 64px;
  margin-bottom: 18px;
  padding: 0 18px;
  background: #ffffff;
  border: 1px solid #d8dce3;
  border-radius: 16px;
  box-sizing: border-box;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.field-box:focus-within {
  border-color: #ff7a00;
  box-shadow: 0 0 0 4px rgba(255, 122, 0, 0.08);
}

.field-icon {
  width: 28px;
  margin-right: 12px;
  font-size: 22px;
  font-weight: 600;
  color: #b7bcc7;
  text-align: center;
  flex-shrink: 0;
}

.login-field {
  flex: 1;
}

.login-field /deep/ .van-field__body {
  align-items: center;
}

.login-field /deep/ .van-field__control {
  height: 44px;
  font-size: 18px;
  color: #111827;
}

.login-field /deep/ .van-field__control::placeholder {
  color: #b7bcc7;
}

.submit-area {
  padding-top: 8px;
}

.submit-button {
  height: 52px;
  border: none;
  font-size: 17px;
  font-weight: 600;
  box-shadow: 0 10px 20px rgba(255, 122, 0, 0.18);
}

.powered-by {
  margin-top: 28px;
  text-align: center;
  font-size: 11px;
  color: #999;
}

@media (max-width: 480px) {
  .content-area {
    align-items: flex-start;
    padding: 42px 18px 16px;
  }

  .brand-panel {
    margin-bottom: 24px;
  }

  .brand-prefix,
  .brand-accent {
    font-size: 34px;
  }

  .field-box {
    min-height: 56px;
    padding: 0 14px;
    border-radius: 14px;
  }

  .login-field /deep/ .van-field__control {
    font-size: 16px;
  }

  .submit-button {
    height: 50px;
  }

  .powered-by {
    padding: 4px 0 14px;
  }
}
</style>